import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type AppPopupType = 'success' | 'error' | 'confirm';

export interface AppPopupOptions {
  type: AppPopupType;
  title: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  autoCloseMs?: number;
}

export interface AppPopupResult {
  isConfirmed: boolean;
}

@Injectable({ providedIn: 'root' })
export class PopupService {
  static instance: PopupService | null = null;

  private _state: AppPopupOptions | null = null;
  private _stateSubject = new BehaviorSubject<AppPopupOptions | null>(null);
  readonly state$ = this._stateSubject.asObservable();
  private _resolver: ((result: AppPopupResult) => void) | null = null;
  private _timerId: any = null;

  get state(): AppPopupOptions | null {
    return this._state;
  }

  constructor() {
    PopupService.instance = this;
  }

  success(title: string, message?: string, autoCloseMs: number = 2500): Promise<AppPopupResult> {
    return this.open({ type: 'success', title, message, autoCloseMs, confirmText: 'OK' });
  }

  error(title: string, message?: string): Promise<AppPopupResult> {
    return this.open({ type: 'error', title, message, confirmText: 'OK' });
  }

  confirm(title: string, message: string, confirmText: string = 'Yes', cancelText: string = 'Cancel'): Promise<AppPopupResult> {
    return this.open({ type: 'confirm', title, message, confirmText, cancelText });
  }

  open(options: AppPopupOptions): Promise<AppPopupResult> {
    this.clearTimer();

    // If another popup is open, resolve it as cancelled.
    if (this._resolver) {
      this._resolver({ isConfirmed: false });
    }

    this._state = options;
    this._stateSubject.next(this._state);

    return new Promise<AppPopupResult>((resolve) => {
      this._resolver = resolve;

      if (typeof options.autoCloseMs === 'number' && options.autoCloseMs > 0 && options.type !== 'confirm') {
        this._timerId = setTimeout(() => {
          this.close(false);
        }, options.autoCloseMs);
      }
    });
  }

  confirmAction(): void {
    this.close(true);
  }

  cancelAction(): void {
    this.close(false);
  }

  close(isConfirmed: boolean = false): void {
    this.clearTimer();

    const resolver = this._resolver;
    this._resolver = null;
    this._state = null;
    this._stateSubject.next(this._state);

    if (resolver) {
      resolver({ isConfirmed });
    }
  }

  private clearTimer(): void {
    if (this._timerId) {
      clearTimeout(this._timerId);
      this._timerId = null;
    }
  }
}
